import { Link } from "@components/Link";
import { AMPLITUDE_EVENTS } from "@constants/analytics";
import {
  NAV_ICON_ACTIVE_COLOR_CLASSNAME,
  NAV_ICON_COLOR_CLASSNAME,
} from "@constants/layout";
import { LightButton } from "@lyteflo/components";
import type { SVGProps } from "@lyteflo/types";
import { useMobile } from "@lyteflo/utils";
import { trackEvent } from "@services/analytics";
import { navDestinationFromHref } from "@utils/analytics";
import { useMemo } from "preact/hooks";
import { useLocation } from "wouter";

type NavItemProps = {
  name: string;
  href: string;
  Icon: SVGProps;
  iconClassName?: string;
  iconColorClassName?: string;
};

export const NavItem = ({
  name,
  href,
  Icon,
  iconClassName = "",
  iconColorClassName,
}: NavItemProps) => {
  const [location] = useLocation();
  const isMobile = useMobile();
  const active = location === href || location.startsWith(`${href}/`);
  const navColor = active
    ? NAV_ICON_ACTIVE_COLOR_CLASSNAME
    : NAV_ICON_COLOR_CLASSNAME;

  const linkActiveClassName =
    "md:lf-underline md:lf-decoration-[var(--gray-12)] md:lf-text-[var(--gray-12)] md:lf-p-0 md:lf-bg-transparent";

  const navItemContent = useMemo(
    () => (
      <>
        <Icon
          className={`lf-shrink-0 lf-w-8 lf-h-8 md:lf-mr-1 ${iconColorClassName ?? navColor} ${iconClassName}`}
        />
        <span
          className={`lf-leading-none lf-text-xs md:lf-block md:lf-text-lg ${navColor}`}
        >
          {name}
        </span>
      </>
    ),
    [name, iconClassName, iconColorClassName, navColor],
  );

  const handleNavClick = () => {
    trackEvent(AMPLITUDE_EVENTS.CLICK_NAV, {
      destination: navDestinationFromHref(href),
    });
  };

  return (
    <Link
      asChild
      key={name}
      href={href}
      color={"gray"}
      onClick={handleNavClick}
      className={`lf-w-fit lf-mx-auto lf-flex lf-flex-col lf-items-center md:lf-mx-0 md:lf-flex-row md:lf-decoration-2 md:lf-underline-offset-4 md:lf-decoration-[var(--gray-12)] md:lf-underline-offset-8 ${active ? linkActiveClassName : ""}`}
    >
      {isMobile ? (
        <LightButton
          variant="ghost"
          className={`lf-flex lf-flex-col lf-gap-0 lf-rounded-lg lf-shadow-none ${active ? "lf-bg-[var(--green-9)]" : ""} `}
        >
          {navItemContent}
        </LightButton>
      ) : (
        navItemContent
      )}
    </Link>
  );
};
